// ============================================================================
// UNIFIED PAYMENT VOUCHER types (ADR-0039).
//
// One voucher covers bill settlement, on-account advances, direct expenses and
// recoverable expenses — with withholding, discounts, tax adjustments and
// round-off as ordinary lines. The cash paid is DERIVED, never typed:
//
//     net paid = gross - deductions + round-off
// ============================================================================

/**
 * Line kinds (ADR-0041). `amount` is always positive; party lines always add to the cash
 * paid, and a GL line's direction comes from `isCredit` rather than from its kind.
 */
export const PAYMENT_LINE_TYPES = {
  BILL_SETTLEMENT: 'BILL_SETTLEMENT',
  ON_ACCOUNT: 'ON_ACCOUNT',
  RECOVERABLE_EXPENSE: 'RECOVERABLE_EXPENSE',
  /** Any nominal account — expense, asset, deposit, withholding, discount, round-off. */
  GL_LINE: 'GL_LINE',
} as const;

export type PaymentLineType = typeof PAYMENT_LINE_TYPES[keyof typeof PAYMENT_LINE_TYPES];

/** Party-side lines: they carry allocation semantics and always increase the payment. */
export const isPaymentPartyLine = (t: string): boolean =>
  t === PAYMENT_LINE_TYPES.BILL_SETTLEMENT || t === PAYMENT_LINE_TYPES.ON_ACCOUNT
  || t === PAYMENT_LINE_TYPES.RECOVERABLE_EXPENSE;

/** True when the line's direction is carried by `isCredit` rather than its kind. */
export const isPaymentDirectionalLine = (t: string): boolean =>
  t === PAYMENT_LINE_TYPES.GL_LINE;

export interface PaymentLineDto {
  id: string;
  lineNo: number;
  lineType: string;
  ledgerId?: string | null;
  ledgerCode?: string | null;
  ledgerName?: string | null;
  partyId?: string | null;
  partyName?: string | null;
  businessUnitId?: string | null;
  businessUnitName?: string | null;
  targetEntityId?: string | null;
  targetEntityCode?: string | null;
  serviceId?: string | null;
  serviceName?: string | null;
  settledOpenItemId?: string | null;
  settledReferenceNo?: string | null;
  /** ON_ACCOUNT only — SUPPLIER_ADVANCE or CUSTOMER_REFUND (ADR-0041 §4). */
  payAs?: string | null;
  /** Always the entity's own currency — the header totals are derived in base. */
  amount: number;
  /** What was entered, in the payment currency. Null on a home-currency payment (ADR-0041 §9). */
  fcAmount?: number | null;
  /** Realized exchange gain (+) / loss (-) in base, from settling a bill at a rate other than
   *  the one it was booked at. 0 on every home-currency line. */
  realizedFxAmount: number;
  /** Settlements only: the settled bill's own currency. */
  settledCurrency?: string | null;
  /** Settlements only: how much of the bill's own currency was discharged (ADR-0031 revision). */
  settledFcAmount?: number | null;
  isCredit: boolean;
  openItemId?: string | null;
  narration?: string | null;
  /** ADR-0060: the part of the bill closed but kept open as a withheld item — bill currency. */
  withheldFcAmount?: number | null;
  /** The same in base, at the bill's booked rate. */
  withheldAmount?: number | null;
  withheldReasonCode?: string | null;
  withheldReasonLabel?: string | null;
  withheldRemarks?: string | null;
  withheldOpenItemId?: string | null;
}

export interface PaymentDto {
  id: string;
  entityId: string;
  businessUnitId?: string | null;
  businessUnitName?: string | null;
  paymentNumber?: string | null;
  voucherTypeId?: string | null;
  paymentDate: string;
  partyId?: string | null;
  partyName?: string | null;
  paidFromLedgerId: string;
  paidFromLedgerName?: string | null;
  /** What left the bank — the paid-from account's currency. */
  currencyCode?: string | null;
  exchangeRate: number;
  /** The entity's own currency. Every total on this DTO is in it, not in `currencyCode`. */
  baseCurrencyCode?: string | null;

  grossAmount: number;
  deductionTotal: number;
  /** Derived cash actually paid = gross - deductions. Round-off is an ordinary GL line. */
  netPaid: number;

  instrumentType?: string | null;
  instrumentNumber?: string | null;
  instrumentDate?: string | null;
  draweeBank?: string | null;

  narration?: string | null;
  docStatus: string;
  transactionId?: string | null;
  icLink?: string | null;

  statusId?: string | null;
  statusName?: string | null;
  rowVersion: number;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;

  lines: PaymentLineDto[];
}

export interface CreatePaymentLineRequest {
  lineType: string;
  /** Optional where the service resolves it (settlement → the bill's ledger;
   *  discount / rounding / input-tax → the entity's ledger defaults). */
  ledgerId?: string | null;
  partyId?: string | null;
  businessUnitId?: string | null;
  targetEntityId?: string | null;
  serviceId?: string | null;
  settledOpenItemId?: string | null;
  /** ON_ACCOUNT only — SUPPLIER_ADVANCE or CUSTOMER_REFUND. */
  payAs?: string | null;
  amount: number;
  /**
   * BILL_SETTLEMENT where the bill's currency differs from the payment's: how much of the
   * BILL's own currency this settles. Required there — the server refuses without it, because
   * the money alone says nothing about the bill currency once the rate has moved. The exchange
   * difference (paid in base − this at the booked rate) posts to FX Gain / Loss.
   */
  settledFcAmount?: number | null;
  /** Settlement (ADR-0060): bill-currency amount we withhold — closes the bill, stays open as a withheld item. */
  withheldAmount?: number | null;
  /** Required with withheldAmount — DeductionReasonDto.value. */
  withheldReasonCode?: string | null;
  withheldRemarks?: string | null;
  /**
   * GL_LINE only. `true` reduces the cash paid, `false` increases it. Omit to take the
   * direction from the ledger's normal balance (ADR-0041).
   */
  isCredit?: boolean | null;
  narration?: string | null;
}

export interface CreatePaymentRequest {
  paymentDate: string;
  partyId?: string | null;
  paidFromLedgerId: string;
  currencyCode?: string | null;
  exchangeRate?: number;
  instrumentType?: string | null;
  instrumentNumber?: string | null;
  instrumentDate?: string | null;
  draweeBank?: string | null;
  narration?: string | null;
  lines: CreatePaymentLineRequest[];
}

/**
 * A party the entity currently owes. The picker's default list is built from open items
 * rather than from party role, so a customer in credit can be refunded and a supplier whose
 * classification was never set is not hidden (ADR-0041 §5).
 */
export interface PayablePartyDto {
  partyId: string;
  partyCode: string;
  partyName: string;
  partyType?: string | null;
  amountOwed: number;
  openItemCount: number;
  /** What we owe sits on their receivables ledger — a customer in credit. */
  hasCustomerCredit: boolean;
  /** What we owe sits on their payables ledger — unpaid supplier bills. */
  hasSupplierBills: boolean;
}

/** Which relationship an unallocated party payment settles. */
export const PAY_AS = {
  SUPPLIER_ADVANCE: 'SUPPLIER_ADVANCE',
  CUSTOMER_REFUND: 'CUSTOMER_REFUND',
} as const;

export interface PaymentQueryParams {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  partyId?: string;
  fromDate?: string;
  toDate?: string;
  includeCancelled?: boolean;
}
